import type { Variants } from 'framer-motion';
import type { CSSProperties } from 'react';
import styles from './SideMenuView.module.css';
import type { IHamburgerSideMenuViewProperties } from './interface';

const ANIMATION_DURATION_SUBLIST_SHOW = 0.6;
const ANIMATION_DURATION_SUBLIST_HIDE = 0.4;
/* MUST match var(--bounce-width) in CSS. 
    This is the extra space allocated as the view loads in it 'overshoots' x=0 as part of the spring animation
    & so if this is set to 0 you'll get a gap from the left-edge of this view & the screen.
    It's job is to oversize the sideview such that we dont see this gap */
const BOUNCE_WIDTH = '3vw';

export enum StyleElement {
    Container = 'Container',
    ImageContainer = 'ImageContainer',
    Image = 'Image',
    ListContainer = 'ListContainer',
}

export enum SideMenuState {
    Collapsed = 'Collapsed',
    Expanded = 'Expanded',
}

const DEFAULTS: Record<StyleElement, CSSProperties> = {
    [StyleElement.Container]: {},
    [StyleElement.ImageContainer]: {},
    [StyleElement.Image]: {},
    [StyleElement.ListContainer]: {},
};

const RUNTIME: Record<StyleElement, Record<SideMenuState, CSSProperties>> = {
    [StyleElement.Container]: {
        [SideMenuState.Collapsed]: {
            /* Ideally only animate width of view. However this is unknown. So animate the width of this screen */
            left: '-100%',
        },
        [SideMenuState.Expanded]: {
            left: `-${BOUNCE_WIDTH}`,
        },
    },
    [StyleElement.ImageContainer]: {
        [SideMenuState.Collapsed]: {},
        [SideMenuState.Expanded]: {},
    },
    [StyleElement.Image]: {
        [SideMenuState.Collapsed]: {},
        [SideMenuState.Expanded]: {},
    },
    [StyleElement.ListContainer]: {
        [SideMenuState.Collapsed]: {},
        [SideMenuState.Expanded]: {},
    },
};

const PROPS_APPLICATOR: Record<
    StyleElement,
    (properties: IHamburgerSideMenuViewProperties) => CSSProperties
> = {
    [StyleElement.Container]: (properties: IHamburgerSideMenuViewProperties) => ({
        ...RUNTIME[StyleElement.Container][
            properties.isShowing ? SideMenuState.Expanded : SideMenuState.Collapsed
        ],
        backgroundColor: properties.backgroundColor,
    }),
    [StyleElement.ImageContainer]: (properties: IHamburgerSideMenuViewProperties) => ({
        ...RUNTIME[StyleElement.ImageContainer][
            properties.isShowing ? SideMenuState.Expanded : SideMenuState.Collapsed
        ],
    }),
    [StyleElement.Image]: (properties: IHamburgerSideMenuViewProperties) => ({
        ...RUNTIME[StyleElement.Image][
            properties.isShowing ? SideMenuState.Expanded : SideMenuState.Collapsed
        ],
    }),
    [StyleElement.ListContainer]: (properties: IHamburgerSideMenuViewProperties) => ({
        ...RUNTIME[StyleElement.ListContainer][
            properties.isShowing ? SideMenuState.Expanded : SideMenuState.Collapsed
        ],
    }),
};

export function animationVariantsForElement(element: StyleElement): Variants {
    const variants = {
        [SideMenuState.Collapsed]: {
            ...RUNTIME[element][SideMenuState.Collapsed],
            transition: { type: 'spring', bounce: 0.2, duration: ANIMATION_DURATION_SUBLIST_HIDE },
        },
        [SideMenuState.Expanded]: {
            ...RUNTIME[element][SideMenuState.Expanded],
            transition: { type: 'spring', bounce: 0.2, duration: ANIMATION_DURATION_SUBLIST_SHOW },
        },
    };

    /* Cannot Pass CSSProperties to Variants due to 'Omit' in CSSPropertiesWithoutTransitionOrSingleTransforms */
    return variants as Variants;
}

export function animationStateForElement(
    element: StyleElement,
    properties: IHamburgerSideMenuViewProperties,
): string {
    return properties.isShowing ?? false ? SideMenuState.Expanded : SideMenuState.Collapsed;
}

export function classNameForElement(
    element: StyleElement,
    properties: IHamburgerSideMenuViewProperties,
): string {
    const name = element.toString();
    return styles[name] ?? '';
}

export function styleForElement(
    element: StyleElement,
    properties: IHamburgerSideMenuViewProperties,
): CSSProperties {
    const style = { ...DEFAULTS[element], ...PROPS_APPLICATOR[element](properties) };

    return style;
}
